import axios, { type CancelToken } from "axios";
import urlJoin from "url-join";
import type { BaseParams, RouteParams } from "../client.types.js";
import { RequestError, RequestSuccess } from "../request.types.js";

const buildUrl = (baseUrl: string, path: string) => {
    if (path.startsWith("/api") && baseUrl.endsWith("/api")) {
        return urlJoin(baseUrl, path.slice("/api".length));
    }
    return urlJoin(baseUrl, path);
};
const replaceInUrl = (url: string, params: Record<string, unknown>) => {
    let newUrl = url;
    for (const [key, value] of Object.entries(params)) {
        newUrl = newUrl.replace(`:${key}`, `${value}`);
    }
    return newUrl;
};
const catchHandler = <
    T extends {
        code: string;
        message: string;
        raw?: unknown;
    },
>(
    e: unknown,
): RequestError<T> => {
    if (axios.isAxiosError(e)) {
        if (e.response !== undefined) {
            return {
                hasFailed: true,
                status: e.response.status,
                error: {
                    code:
                        typeof e.response?.data?.code === "string" || typeof e.response?.data?.code === "number"
                            ? String(e.response.data.code)
                            : "UNKNOWN_ERROR",
                    message:
                        typeof e.response?.data?.message === "string"
                            ? e.response.data.message
                            : typeof e.response?.data === "string"
                              ? e.response.data
                              : "Unknown error",
                    raw: e.response.data,
                } as T,
            };
        }
        if (e.request !== undefined) {
            return {
                hasFailed: true,
                status: 503,
                error: {
                    code: "SERVICE_UNAVAILABLE",
                    message: "Service unavailable",
                    raw: e.request,
                } as T,
            };
        }
    }
    return {
        hasFailed: true,
        status: 500,
        error: {
            code: "UNKNOWN_ERROR",
            message: typeof e === "string" ? e : e instanceof Error ? e.message : "Unknown error",
            raw: e,
        } as T,
    };
};
export const createAxiosRoute = <
    Request extends {
        body?: unknown;
        query?: unknown;
        params?: Record<string, unknown>;
        headers?: unknown;
        cancelToken?: CancelToken;
    } | void,
    Response,
>({
    baseUrl,
    headers,
    httpAgent,
    httpsAgent,
    path,
    method,
    adapter,
}: BaseParams & RouteParams) => {
    const url = buildUrl(baseUrl, path);
    return async (requestParams: Request): Promise<RequestSuccess<Response> | RequestError> => {
        try {
            const { body, query, params, headers: requestHeaders, cancelToken } = requestParams ?? {};
            const result = await axios.request<Response>({
                method,
                url,
                httpsAgent,
                httpAgent,
                adapter,
                headers,
                cancelToken,
                ...(params ? { url: replaceInUrl(url, params) } : {}),
                ...(query ? { params: query } : {}),
                ...(body ? { data: body } : {}),
                ...(requestHeaders ? { ...headers, ...requestHeaders } : {}),
            });
            return { hasFailed: false, status: result.status, data: result.data };
        } catch (e: unknown) {
            return catchHandler(e);
        }
    };
};
